import React, { KeyboardEvent, useState, useRef, useEffect } from 'react';
import { styled } from '@mui/system';
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { MyButton } from '@/components/uiComponents';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { ReactComponent as CaretDown } from 'src/components/uiComponents/MySelect/CaretDown.svg';

import { AmbassadorFilterValue } from '../types/types';

import { ExportUsersCsvButton } from './ExportUsersCsvButton';

interface IconComponentProps {
  className: string;
}

interface TypesUserProps {
  value: string;
  label: string;
}

const typesUsers = [
  { value: 'all', label: 'All users' },
  { value: 'broker', label: 'Licensed Broker' },
  { value: 'manager', label: 'Agency Manager' },
  { value: 'agency', label: 'Real estate agency' },
  { value: 'developer', label: 'Developer' },
  { value: 'foreign', label: 'Foreign Broker' },
];

const ambassadorFilters = [
  { value: AmbassadorFilterValue.All, label: 'All' },
  { value: AmbassadorFilterValue.Ambassadors, label: 'Ambassadors' },
  { value: AmbassadorFilterValue.NonAmbassadors, label: 'Non-ambassadors' },
];

interface UserSearchProps {
  search: string;
  tabValue: number;
  typeUsers: string;
  ambassadorFilter: AmbassadorFilterValue;
  setTypeUsers: React.Dispatch<React.SetStateAction<string>>;
  setAmbassadorFilter: React.Dispatch<React.SetStateAction<AmbassadorFilterValue>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  onAddNewUser: () => void;
  setUpdateOtpTable: (value: boolean) => void;
}

export const UserSearch: React.FC<UserSearchProps> = ({
  search,
  tabValue,
  typeUsers,
  ambassadorFilter,
  setTypeUsers,
  setAmbassadorFilter,
  setSearch,
  onAddNewUser,
  setUpdateOtpTable,
}) => {
  const StyledRow = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginBottom: '12px',
    gap: '12px',
    [theme.breakpoints.down('lg')]: {
      flexWrap: 'wrap',
    },
  }));

  const SearchFieldStyle = styled(TextField)({
    width: '100%',
    minWidth: 'min(240px, 100%)',
    flex: '1 1 320px',
  });

  const FixedControl = styled('div')({
    flex: '0 0 auto',
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputAddressValue, setInputAddressValue] = useState<string>(search);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputAddressValue]);

  const handleAddressPressEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setSearch(inputAddressValue);
    }
  };

  const handleChangeTypeUser = (event: SelectChangeEvent) => {
    setTypeUsers(event.target.value as string);
  };

  const handleChangeAmbassadorFilter = (event: SelectChangeEvent) => {
    setAmbassadorFilter(event.target.value as AmbassadorFilterValue);
  };

  const placeholder =
    tabValue === 3 ? 'Search by phone or email' : 'Search by name, phone or email';

  return (
    <StyledRow data-qa='admin-users-search-toolbar'>
      {tabValue === 0 && (
        <Select
          sx={{
            maxWidth: '190px',
            width: '100%',
            flex: '0 0 190px',
          }}
          MenuProps={{
            sx: {
              marginTop: '4px',
            },
          }}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={typeUsers}
          onChange={(event) => handleChangeTypeUser(event)}
          IconComponent={(_props: IconComponentProps) => {
            const rotate = _props.className.toString().includes('iconOpen');
            return (
              <div
                data-qa='dropdown_menu_btn'
                style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  pointerEvents: 'none',
                  right: 15,
                  height: '15px',
                  width: '15px',
                  transform: rotate ? 'rotate(180deg)' : 'none',
                }}
              >
                <CaretDown />
              </div>
            );
          }}
        >
          {typesUsers.map((typeUser: TypesUserProps, index) => (
            <MenuItem
              key={typeUser.value}
              sx={{
                borderBottom: index !== typesUsers.length - 1 ? '1px solid #e0e0e0' : '',
                padding: '11px 17px',
              }}
              value={typeUser.value}
            >
              {typeUser.label}
            </MenuItem>
          ))}
        </Select>
      )}
      {(tabValue === 0 || tabValue === 1) && (
        <Select
          inputProps={{ 'aria-label': 'Ambassador filter' }}
          sx={{
            maxWidth: '205px',
            width: '100%',
            flex: '0 0 205px',
          }}
          MenuProps={{
            sx: {
              marginTop: '4px',
            },
          }}
          value={ambassadorFilter}
          onChange={handleChangeAmbassadorFilter}
          IconComponent={(_props: IconComponentProps) => {
            const rotate = _props.className.toString().includes('iconOpen');
            return (
              <div
                data-qa='ambassador_dropdown_menu_btn'
                style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  pointerEvents: 'none',
                  right: 15,
                  height: '15px',
                  width: '15px',
                  transform: rotate ? 'rotate(180deg)' : 'none',
                }}
              >
                <CaretDown />
              </div>
            );
          }}
        >
          {ambassadorFilters.map((filter, index) => (
            <MenuItem
              key={filter.value}
              sx={{
                borderBottom: index !== ambassadorFilters.length - 1 ? '1px solid #e0e0e0' : '',
                padding: '11px 17px',
              }}
              value={filter.value}
            >
              {filter.label}
            </MenuItem>
          ))}
        </Select>
      )}
      <SearchFieldStyle
        placeholder={placeholder}
        inputProps={{ ref: inputRef }}
        value={inputAddressValue}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setInputAddressValue(event.target.value);
        }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleAddressPressEnter}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <IconButton
              sx={{
                visibility: inputAddressValue ? 'visible' : 'hidden',
              }}
              onClick={() => {
                setSearch('');
                setInputAddressValue('');
              }}
            >
              <ClearIcon />
            </IconButton>
          ),
        }}
      />

      <FixedControl data-qa='admin-users-fixed-control'>
        <MyButton
          data={{
            buttonName: 'Search',
            styleType: 'button',
            variant: 'contained',
            customWidth: '146px',
          }}
          onClick={() => {
            setSearch(inputAddressValue);
            if (tabValue === 3) {
              setUpdateOtpTable(true);
            }
          }}
        />
      </FixedControl>
      {tabValue === 0 && (
        <>
          <FixedControl data-qa='admin-users-fixed-control'>
            <MyButton
              data={{
                buttonName: 'Add new user',
                styleType: 'cancel',
                variant: 'contained',
                customWidth: '156px',
                sx: {
                  whiteSpace: 'nowrap',
                },
              }}
              onClick={onAddNewUser}
            />
          </FixedControl>
          <FixedControl data-qa='admin-users-fixed-control'>
            <ExportUsersCsvButton />
          </FixedControl>
        </>
      )}
    </StyledRow>
  );
};
